import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import * as FaIcons from 'react-icons/fa';
import SafeIcon from '../common/SafeIcon';

const { FiX, FiSend } = FiIcons;
const { FaWhatsapp } = FaIcons;

const Chatbot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [input, setInput] = useState('');
  const [showMsg, setShowMsg] = useState(false);
  const [currentTime, setCurrentTime] = useState('');
  const inputRef = useRef(null);

  // Set initial time
  useEffect(() => {
    setCurrentTime(new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }));
  }, [isOpen]);

  // Typing effect logic
  useEffect(() => {
    if (isOpen && !showMsg) {
      const timer = setTimeout(() => {
        setShowMsg(true);
      }, 1500); // 1.5 detik efek mengetik
      return () => clearTimeout(timer);
    }
  }, [isOpen, showMsg]);

  // Auto focus input after message shows
  useEffect(() => {
    if (isOpen && showMsg && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen, showMsg]);

  // Handle send to WhatsApp
  const handleSend = (e) => {
    e.preventDefault();
    if (!input.trim()) return;
    
    const text = encodeURIComponent(input);
    const waNumber = '628211317248'; // Diperbarui sesuai permintaan
    
    // Buka WA di tab baru
    window.open(`https://wa.me/${waNumber}?text=${text}`, '_blank');
    
    // Reset dan tutup widget (opsional)
    setInput('');
    setIsOpen(false);
    setShowMsg(false); // Reset typing status untuk next open
  };

  return (
    <div className="fixed bottom-6 right-6 z-50">
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.9 }}
            className="mb-4 w-[320px] bg-white rounded-2xl shadow-2xl overflow-hidden border border-slate-200 flex flex-col"
          >
            {/* Header bergaya WhatsApp */}
            <div className="bg-[#075E54] p-4 flex justify-between items-center text-white">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center relative shrink-0">
                  <SafeIcon icon={FaWhatsapp} className="text-2xl" />
                  <span className="absolute bottom-0 right-0 w-3 h-3 bg-[#25D366] border-2 border-[#075E54] rounded-full"></span>
                </div>
                <div>
                  <h4 className="text-sm font-semibold leading-tight">Admin Nusantara</h4>
                  <p className="text-[11px] text-white/80 mt-0.5">
                    {showMsg ? 'Online' : 'mengetik...'}
                  </p>
                </div>
              </div>
              <button 
                onClick={() => setIsOpen(false)} 
                className="text-white/70 hover:text-white transition-colors p-1"
              >
                <SafeIcon icon={FiX} className="text-xl" />
              </button>
            </div>

            {/* Body Chat */}
            <div 
              className="h-72 bg-[#E5DDD5] p-4 overflow-y-auto flex flex-col gap-3 text-sm relative"
              style={{ 
                backgroundImage: 'url("https://user-images.githubusercontent.com/15075759/28719144-86dc0f70-73b1-11e7-911d-60d70fcded21.png")',
                backgroundSize: 'cover',
                backgroundBlendMode: 'overlay',
                backgroundColor: 'rgba(229, 221, 213, 0.9)'
              }}
            >
              {!showMsg ? (
                // Typing Indicator
                <div className="bg-white p-3 rounded-2xl rounded-tl-none shadow-sm w-16 self-start flex items-center justify-center gap-1.5 h-10">
                  <motion.span animate={{ y: [0, -4, 0] }} transition={{ repeat: Infinity, duration: 0.8, delay: 0 }} className="w-2 h-2 bg-slate-400 rounded-full"></motion.span>
                  <motion.span animate={{ y: [0, -4, 0] }} transition={{ repeat: Infinity, duration: 0.8, delay: 0.2 }} className="w-2 h-2 bg-slate-400 rounded-full"></motion.span>
                  <motion.span animate={{ y: [0, -4, 0] }} transition={{ repeat: Infinity, duration: 0.8, delay: 0.4 }} className="w-2 h-2 bg-slate-400 rounded-full"></motion.span>
                </div>
              ) : (
                // Actual Message Bubble
                <motion.div 
                  initial={{ opacity: 0, scale: 0.9, transformOrigin: 'top left' }} 
                  animate={{ opacity: 1, scale: 1 }} 
                  className="bg-white px-3 py-2.5 rounded-2xl rounded-tl-none shadow-sm max-w-[85%] self-start text-[#111b21] relative pb-6"
                >
                  <span className="leading-relaxed">
                    Halo! Saya Admin Nusantara Logistik. Ada yang bisa saya bantu terkait layanan pengiriman atau cek tarif hari ini?
                  </span>
                  <span className="text-[10px] text-slate-400 absolute bottom-1.5 right-3">
                    {currentTime}
                  </span>
                </motion.div>
              )}
            </div>

            {/* Input Form */}
            <form onSubmit={handleSend} className="p-3 bg-[#F0F0F0] flex items-center gap-2">
              <input
                ref={inputRef}
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Ketik balasan untuk WA..."
                className="flex-1 bg-white border-none rounded-full px-4 py-3 text-sm focus:outline-none shadow-sm placeholder:text-slate-500 text-slate-800"
                disabled={!showMsg}
              />
              <button
                type="submit"
                disabled={!input.trim() || !showMsg}
                className="w-11 h-11 rounded-full bg-[#00A884] text-white flex items-center justify-center hover:bg-[#075E54] transition-colors disabled:bg-slate-300 disabled:cursor-not-allowed shrink-0 shadow-sm"
              >
                <SafeIcon icon={FiSend} className="ml-1 text-lg" />
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Floating Toggle Button */}
      <motion.button
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        onClick={() => {
          setIsOpen(!isOpen);
          if (isOpen) setShowMsg(false); // Reset if closed manually
        }}
        className={`w-14 h-14 ${isOpen ? 'bg-slate-700 hover:bg-slate-800' : 'bg-[#25D366] hover:bg-[#128C7E]'} text-white rounded-full flex items-center justify-center shadow-xl transition-colors relative z-10`}
      >
        <SafeIcon icon={isOpen ? FiX : FaWhatsapp} className={isOpen ? 'text-2xl' : 'text-3xl'} />
      </motion.button>
    </div>
  );
};

export default Chatbot;